import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MessageCircle, Plus, Minus, ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BUSINESS, PRODUCT_OPTIONS, WHATSAPP_URL } from "@/lib/constants";
import { track, EVENTS } from "@/lib/analytics";
import { useEnquiry } from "@/context/EnquiryContext";
import { Reveal } from "@/components/Reveal";

const GST_OPTIONS = ["Yes – GST invoice required", "No"];

const INITIAL_FORM = {
  buyer_name: "",
  business_name: "",
  mobile: "",
  city: "",
  product: PRODUCT_OPTIONS[0],
  quantity_kg: "",
  message: "",
  grade_spec: "",
  email: "",
  gst_invoice: GST_OPTIONS[0],
  purchase_date: "",
  enquiry_type: "Quotation",
  consent: false,
  website: "",
};

const inputClass =
  "w-full px-3 py-2.5 text-sm border border-[#D0CBC0] rounded-[8px] bg-white text-spice-ink transition-colors focus:outline-none focus:border-spice-green focus:ring-2 focus:ring-spice-green/25";
const labelClass = "block text-xs font-semibold text-[#24342C] mb-1.5";

export const EnquirySection = ({ compactHeading = false }) => {
  const { prefill, clearPrefill } = useEnquiry();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [showOptional, setShowOptional] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!prefill) return;
    setResult(null);
    setForm((prev) => ({
      ...prev,
      product: prefill.product || prev.product,
      grade_spec: prefill.grade || prev.grade_spec,
      enquiry_type: prefill.enquiryType || "Quotation",
      message:
        prefill.enquiryType === "Actual-Lot Sample"
          ? prev.message || "Please share an actual-lot sample / lot photographs for my requirement."
          : prev.message,
    }));
    if (prefill.grade) setShowOptional(true);
    clearPrefill();
  }, [prefill, clearPrefill]);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    if (!startedRef.current) {
      startedRef.current = true;
      track(EVENTS.formStart, {});
    }
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    const next = {};
    const digits = form.mobile.replace(/\D/g, "");
    const local = digits.length === 12 && digits.startsWith("91") ? digits.slice(2) : digits;
    if (!/^[6-9]\d{9}$/.test(local)) next.mobile = "Enter a valid 10-digit Indian mobile number (+91 accepted).";
    const qty = parseFloat(form.quantity_kg);
    if (!(qty > 0)) next.quantity_kg = "Enter an approximate quantity greater than zero.";
    if (form.message.trim().length < 3) next.message = "Please describe your requirement briefly.";
    if (!form.consent) next.consent = "Please accept the consent statement to continue.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) {
      toast.error("Please correct the highlighted fields.");
      track(EVENTS.formSubmitError, { reason: "validation" });
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const { data } = await api.post("/enquiries", {
        ...form,
        quantity_kg: `${form.quantity_kg} kg`,
        source_page: window.location.pathname,
      });
      setResult(data);
      setForm(INITIAL_FORM);
      setShowOptional(false);
      startedRef.current = false;
      track(EVENTS.formSubmitSuccess, { product: data?.reference ? "captured" : "unknown" });
    } catch (err) {
      track(EVENTS.formSubmitError, { reason: "server" });
      const detail =
        formatApiErrorDetail(err.response?.data?.detail) ||
        "We could not submit your enquiry just now. Your details are still here — please try again, or reach us on WhatsApp or by phone.";
      setSubmitError(detail);
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-20 bg-spice-green-dark text-white px-4 sm:px-6" data-testid="enquiry-section">
      <div className="max-w-4xl mx-auto space-y-8">
        <span id="enquiry-form-anchor" className="block -mt-24 pt-24" aria-hidden="true"></span>

        <Reveal className="space-y-3 max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-widest text-spice-gold">Request current quote</span>
          <h2 className={`${compactHeading ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"} font-extrabold`}>
            Tell Us the Grade and Quantity You Need
          </h2>
          <p className="text-sm text-spice-sage">
            Seven quick details are enough. We check the current lot and respond with availability, specification and a
            written quotation.
          </p>
        </Reveal>

        <Reveal>
          {result ? (
            <div className="bg-white text-spice-ink p-6 sm:p-10 rounded-xl shadow-xl space-y-4 text-center" data-testid="enquiry-success-message">
              <CheckCircle2 className="w-12 h-12 text-spice-green mx-auto" aria-hidden="true" />
              <h3 className="text-lg font-bold">Enquiry received</h3>
              <p className="text-sm text-spice-muted max-w-xl mx-auto leading-relaxed">{result.message}</p>
              <p className="text-sm font-semibold" data-testid="enquiry-reference">
                Your enquiry reference: <span className="text-spice-green">{result.reference}</span>
              </p>
              <p className="text-xs text-spice-subtle">
                We normally respond during working hours ({BUSINESS.hours}). Please keep this reference for follow-up.
              </p>
              <div className="flex flex-wrap justify-center gap-3 pt-2">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Continue this enquiry on WhatsApp (opens WhatsApp in a new tab)"
                  onClick={() => track(EVENTS.whatsappClick, { source: "enquiry-success" })}
                  data-testid="enquiry-whatsapp-followup"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-spice-green text-white text-xs font-bold rounded-[9px] hover:bg-spice-green-dark transition-colors"
                >
                  <MessageCircle className="w-4 h-4" aria-hidden="true" />
                  Continue on WhatsApp
                </a>
                <Link
                  to="/products"
                  data-testid="enquiry-back-to-products"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-spice-sand text-spice-green text-xs font-bold rounded-[9px] hover:bg-spice-line transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                  Return to Products
                </Link>
                <button
                  type="button"
                  onClick={() => setResult(null)}
                  data-testid="enquiry-new-button"
                  className="px-5 py-2.5 border border-spice-line text-spice-muted text-xs font-bold rounded-[9px] hover:bg-spice-cream transition-colors"
                >
                  Submit another enquiry
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} id="enquiry-form" method="post" noValidate autoComplete="on" className="bg-white text-spice-ink p-5 sm:p-8 rounded-xl shadow-xl space-y-5" data-testid="enquiry-form">
              {/* Spam honeypot — off-screen, zero visible area, not focusable, not announced */}
              <div
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", top: "0", width: "1px", height: "1px", overflow: "hidden" }}
              >
                <input
                  id="enquiry-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  aria-hidden="true"
                  autoComplete="off"
                  value={form.website}
                  onChange={set("website")}
                  data-testid="enquiry-honeypot"
                  style={{ width: "1px", height: "1px", opacity: 0, border: 0, padding: 0 }}
                />
              </div>

              {form.enquiry_type === "Actual-Lot Sample" && (
                <div className="text-xs font-semibold text-spice-green bg-spice-sand border border-spice-line rounded px-3 py-2" data-testid="enquiry-type-banner">
                  Sample request — we will share actual-lot photographs or dispatch a sample.
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="enquiry-buyer-name">Contact name <span className="text-red-700">*</span></label>
                  <input id="enquiry-buyer-name" name="buyer_name" type="text" required autoComplete="name" className={inputClass} placeholder="e.g. Ramesh Kumar" value={form.buyer_name} onChange={set("buyer_name")} data-testid="enquiry-buyer-name-input" />
                </div>
                <div>
                  <label className={labelClass} htmlFor="enquiry-business-name">Business name <span className="text-red-700">*</span></label>
                  <input id="enquiry-business-name" name="business_name" type="text" required autoComplete="organization" className={inputClass} placeholder="e.g. Sri Balaji Traders" value={form.business_name} onChange={set("business_name")} data-testid="enquiry-business-name-input" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="enquiry-mobile">Mobile / WhatsApp number <span className="text-red-700">*</span></label>
                  <input id="enquiry-mobile" name="mobile" type="tel" inputMode="tel" required autoComplete="tel" aria-invalid={!!errors.mobile} aria-describedby={errors.mobile ? "enquiry-mobile-error" : undefined} className={inputClass} placeholder="+91 98765 43210" value={form.mobile} onChange={set("mobile")} data-testid="enquiry-mobile-input" />
                  {errors.mobile && <p id="enquiry-mobile-error" className="text-[11px] text-red-700 mt-1" data-testid="enquiry-mobile-error">{errors.mobile}</p>}
                </div>
                <div>
                  <label className={labelClass} htmlFor="enquiry-city">City <span className="text-red-700">*</span></label>
                  <input id="enquiry-city" name="city" type="text" required autoComplete="address-level2" className={inputClass} placeholder="Hyderabad" value={form.city} onChange={set("city")} data-testid="enquiry-city-input" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="enquiry-product">Product required <span className="text-red-700">*</span></label>
                  <select id="enquiry-product" name="product" required className={inputClass} value={form.product} onChange={set("product")} data-testid="enquiry-product-select">
                    {PRODUCT_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass} htmlFor="enquiry-quantity">Approximate quantity <span className="text-red-700">*</span></label>
                  <div className="relative">
                    <input id="enquiry-quantity" name="quantity_kg" type="number" min="1" step="1" inputMode="numeric" required aria-invalid={!!errors.quantity_kg} aria-describedby={errors.quantity_kg ? "enquiry-quantity-error" : undefined} className={`${inputClass} pr-12`} placeholder="50" value={form.quantity_kg} onChange={set("quantity_kg")} data-testid="enquiry-quantity-input" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-spice-subtle pointer-events-none">kg</span>
                  </div>
                  {errors.quantity_kg && <p id="enquiry-quantity-error" className="text-[11px] text-red-700 mt-1" data-testid="enquiry-quantity-error">{errors.quantity_kg}</p>}
                </div>
              </div>

              <div>
                <label className={labelClass} htmlFor="enquiry-message">Requirement details <span className="text-red-700">*</span></label>
                <textarea id="enquiry-message" name="message" rows="3" required aria-invalid={!!errors.message} aria-describedby={errors.message ? "enquiry-message-error" : undefined} className={inputClass} placeholder="Grade preference, packing, delivery location or any specific requirement" value={form.message} onChange={set("message")} data-testid="enquiry-message-input" />
                {errors.message && <p id="enquiry-message-error" className="text-[11px] text-red-700 mt-1" data-testid="enquiry-message-error">{errors.message}</p>}
              </div>

              <button
                type="button"
                onClick={() => setShowOptional((v) => !v)}
                aria-expanded={showOptional}
                data-testid="enquiry-optional-toggle"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-spice-green hover:text-spice-green-dark transition-colors"
              >
                {showOptional ? <Minus className="w-3.5 h-3.5" aria-hidden="true" /> : <Plus className="w-3.5 h-3.5" aria-hidden="true" />}
                {showOptional ? "Hide optional details" : "Add optional details (grade, email, GST, timeline)"}
              </button>

              {showOptional && (
                <div className="grid sm:grid-cols-2 gap-4 pt-1" data-testid="enquiry-optional-fields">
                  <div>
                    <label className={labelClass} htmlFor="enquiry-grade">Grade / specification</label>
                    <input id="enquiry-grade" name="grade_spec" type="text" className={inputClass} placeholder="e.g. 7.5–8 mm" value={form.grade_spec} onChange={set("grade_spec")} data-testid="enquiry-grade-input" />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="enquiry-email">Email address</label>
                    <input id="enquiry-email" name="email" type="email" autoComplete="email" className={inputClass} placeholder="name@business.com" value={form.email} onChange={set("email")} data-testid="enquiry-email-input" />
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="enquiry-gst">GST invoice required</label>
                    <select id="enquiry-gst" name="gst_invoice" className={inputClass} value={form.gst_invoice} onChange={set("gst_invoice")} data-testid="enquiry-gst-select">
                      {GST_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass} htmlFor="enquiry-purchase-date">Expected purchase date</label>
                    <input id="enquiry-purchase-date" name="purchase_date" type="text" className={inputClass} placeholder="Immediate / within 7 days" value={form.purchase_date} onChange={set("purchase_date")} data-testid="enquiry-purchase-date-input" />
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2.5 pt-1">
                <input
                  type="checkbox"
                  id="enquiry-consent"
                  name="consent"
                  required
                  checked={form.consent}
                  onChange={set("consent")}
                  aria-invalid={!!errors.consent}
                  aria-describedby={errors.consent ? "enquiry-consent-error" : undefined}
                  className="mt-0.5 w-4 h-4 rounded accent-spice-green shrink-0"
                  data-testid="enquiry-consent-checkbox"
                />
                <div>
                  <label htmlFor="enquiry-consent" className="text-[11px] text-spice-muted leading-relaxed block">
                    I agree to be contacted by Nandanaaya Spices regarding this enquiry and understand that price, grade
                    and availability will be confirmed against the current lot and written quotation.{" "}
                    <Link to="/privacy-policy" className="text-spice-green font-semibold underline underline-offset-2" data-testid="enquiry-privacy-link">Privacy Policy</Link>
                    {" · "}
                    <Link to="/terms-and-conditions" className="text-spice-green font-semibold underline underline-offset-2" data-testid="enquiry-terms-link">Terms &amp; Conditions</Link>
                  </label>
                  {errors.consent && <p id="enquiry-consent-error" className="text-[11px] text-red-700 mt-1" data-testid="enquiry-consent-error">{errors.consent}</p>}
                </div>
              </div>

              {submitError && (
                <div role="alert" className="text-xs text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2.5 leading-relaxed" data-testid="enquiry-submit-error">
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="enquiry-submit-button"
                className="w-full py-3.5 bg-spice-green text-white font-bold text-sm rounded-[9px] hover:bg-spice-green-dark transition-colors shadow-md disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                {submitting ? "Submitting…" : "Request Current Quote"}
              </button>
            </form>
          )}
        </Reveal>

        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-spice-sage border-t border-white/10 pt-6">
          <div>
            Direct proprietor line:{" "}
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              onClick={() => track(EVENTS.phoneClick, { source: "enquiry-section" })}
              className="text-white font-bold hover:text-spice-gold transition-colors"
              data-testid="contact-phone-link"
            >
              {BUSINESS.phoneDisplay}
            </a>
          </div>
          <div>
            Business email:{" "}
            <a
              href={`mailto:${BUSINESS.email}`}
              onClick={() => track(EVENTS.emailClick, { source: "enquiry-section" })}
              className="text-white font-bold hover:text-spice-gold transition-colors break-all"
              data-testid="contact-email-link"
            >
              {BUSINESS.email}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
